import React from 'react'
import { ConsequenceAndProbabilityRisk, RiskLevel, RiskStatus, SWOTType } from '../../api/schemas/schema'
import { Badge, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components'
import { translateEnumValue } from '../../helpers/enumHelper'
import { getSwotBadgeStyle } from '../../helpers/swotHelper'
import { getRiskAssessmentBadgeStyle, getRiskLevelBadgeStyle } from '../../helpers/riskAnalysisHelper'
import { t } from '../../i18n/i18n'
import { calculateRiskLevelValue } from '../../helpers/riskManagementHelper'

const useStyles = makeStyles({
   swotBadge: {
      width: '70px',
   },
   riskAssessmentBadge: {
      minWidth: '90px',
      minHeight: '25px',
   },
   riskLevelBadge: {
      minWidth: '110px',
      minHeight: '25px',
   },
   badgeLarge: {
      minWidth: '120px',
      minHeight: '25px',
   },
   mediumBadge: {
      width: 'auto',
      minHeight: '25px',
   },
   smallBadge: {
      minWidth: '35px',
      minHeight: '25px',
   },
   badgeSpacing: {
      display: 'flex',
      gap: tokens.spacingHorizontalM,
   },
})

type SwotTypeBadgeProps = {
   type: SWOTType
   className?: string
}

export const SwotTypeBadge = ({ type, className }: SwotTypeBadgeProps) => {
   const classes = useStyles()

   return (
      <Badge size="large" appearance="filled" shape="rounded" className={mergeClasses(classes.swotBadge, className ?? '')} style={getSwotBadgeStyle(type)}>
         {translateEnumValue(SWOTType, type)}
      </Badge>
   )
}

type RiskAssessmentBadgeProps = {
   status: RiskStatus
   className?: string
   largeBadge?: boolean
   mediumBadge?: boolean
}
export const RiskAssessmentBadge = ({ status, className, largeBadge, mediumBadge }: RiskAssessmentBadgeProps) => {
   const classes = useStyles()

   return (
      <Badge
         appearance="filled"
         shape="rounded"
         className={mergeClasses(largeBadge ? classes.badgeLarge : mediumBadge ? classes.mediumBadge : classes.riskAssessmentBadge, className ?? '')}
         style={getRiskAssessmentBadgeStyle(status)}
      >
         {translateEnumValue(RiskStatus, status)}
      </Badge>
   )
}

type RiskLevelBadgeProps = {
   riskLevel: RiskLevel
   className?: string
   largeBadge?: boolean
   mediumBadge?: boolean
}
export const RiskLevelBadge = ({ riskLevel, className, largeBadge, mediumBadge }: RiskLevelBadgeProps) => {
   const classes = useStyles()
   if (riskLevel === null || riskLevel === undefined || isNaN(riskLevel)) {
      return <Text italic>{t('NoValueSet')}</Text>
   }
   return (
      <Badge
         appearance="filled"
         shape="rounded"
         className={mergeClasses(largeBadge ? classes.badgeLarge : mediumBadge ? classes.mediumBadge : classes.riskLevelBadge, className ?? '')}
         style={getRiskLevelBadgeStyle(riskLevel)}
      >
         {translateEnumValue(RiskLevel, riskLevel)}
      </Badge>
   )
}

type RiskLevelWithNumberBadgeProps = {
   risk: ConsequenceAndProbabilityRisk
   className?: string
   largeBadge?: boolean
   mediumBadge?: boolean
}
export const RiskLevelWithNumberBadge = ({ risk, className, largeBadge, mediumBadge }: RiskLevelWithNumberBadgeProps) => {
   const classes = useStyles()
   if (risk.riskLevel === null || risk.riskLevel === undefined) {
      return <Text italic>{t('NoValueSet')}</Text>
   }

   const riskLevelValue = calculateRiskLevelValue(risk)

   return (
      <div className={classes.badgeSpacing}>
         <Badge appearance="filled" shape="rounded" className={mergeClasses(classes.smallBadge)} style={getRiskLevelBadgeStyle(risk.riskLevel)}>
            {riskLevelValue}
         </Badge>
         <RiskLevelBadge riskLevel={risk.riskLevel} className={className} largeBadge={largeBadge} mediumBadge={mediumBadge} />
      </div>
   )
}
